import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { Menu, X, ChevronDown, User, LogOut, LayoutDashboard } from 'lucide-react';

const Navbar = () => {
    const { isAuthenticated, user, logout } = useContext(AuthContext);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
        setDropdownOpen(false);
    }, [location]);

    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        setDropdownOpen(false);
        navigate('/login');
    };

    const getDashboardLink = () => {
        switch (user?.role) {
            case 'admin': return '/admin-dashboard';
            case 'co-admin': return '/coadmin';
            case 'accountant': return '/accountant';
            default: return '/dashboard';
        }
    };

    const isActive = (path) => location.pathname === path;

    const linkClass = (path) => `
        px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200
        ${isActive(path) 
            ? 'text-indigo-600 bg-indigo-50/50' 
            : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'}
    `;

    const navLinks = (
        <>
            <Link to="/" className={linkClass('/')}>Home</Link>
            <Link to="/internships" className={linkClass('/internships')}>Internships</Link>
            <Link to="/about" className={linkClass('/about')}>About Us</Link>
            <Link to="/verify-certificate" className={linkClass('/verify-certificate')}>Verify Certificate</Link>
        </>
    );

    return (
        <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/80 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16 sm:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group shrink-0">
                        <div className="relative">
                            <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-500 opacity-20 blur-sm group-hover:opacity-40 transition-all"></div>
                            <img src="/logo192.png" alt="Student Era Logo" className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-100 shadow-sm object-contain p-0.5 bg-white" />
                        </div>
                        <span className="font-extrabold text-slate-900 tracking-tight text-lg sm:text-xl hidden sm:block">Student Era</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-2">
                        {navLinks}
                    </div>

                    {/* Desktop Auth Section */}
                    <div className="hidden md:flex items-center gap-3">
                        {!isAuthenticated ? (
                            <>
                                <Link to="/login" className="px-4.5 py-2 rounded-xl text-slate-700 font-bold text-sm hover:bg-slate-50 hover:text-slate-900 transition-all duration-200">
                                    Login
                                </Link>
                                <Link to="/register" className="btn-navbar-uiverse">
                                    <svg
                                        height="20"
                                        width="20"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M0 0h24v24H0z" fill="none"></path>
                                        <path
                                            d="M5 13c0-5.088 2.903-9.436 7-11.182C16.097 3.564 19 7.912 19 13c0 .823-.076 1.626-.22 2.403l1.94 1.832a.5.5 0 0 1 .095.603l-2.495 4.575a.5.5 0 0 1-.793.114l-2.234-2.234a1 1 0 0 0-.707-.293H9.414a1 1 0 0 0-.707.293l-2.234 2.234a.5.5 0 0 1-.793-.114l-2.495-4.575a.5.5 0 0 1 .095-.603l1.94-1.832C5.077 14.626 5 13.823 5 13zm1.476 6.696l.817-.817A3 3 0 0 1 9.414 18h5.172a3 3 0 0 1 2.121.879l.817.817.982-1.8-1.1-1.04a2 2 0 0 1-.593-1.82c.124-.664.187-1.345.187-2.036 0-3.87-1.995-7.3-5-8.96C8.995 5.7 7 9.13 7 13c0 .691.063 1.372.187 2.037a2 2 0 0 1-.593 1.82l-1.1 1.039.982 1.8zM12 13a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
                                            fill="currentColor"
                                        ></path>
                                    </svg>
                                    <span>Register</span>
                                </Link>
                            </>
                        ) : (
                            <div className="relative" ref={dropdownRef}>
                                <button 
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200"
                                >
                                    <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm overflow-hidden">
                                        {user?.avatar ? (
                                            <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            user?.name?.charAt(0)?.toUpperCase() || 'U'
                                        )}
                                    </div>
                                    <span className="font-bold text-slate-800 text-sm hidden lg:inline max-w-[100px] truncate">{user?.name}</span>
                                    <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {dropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-slate-200 shadow-xl py-2 z-50 animate-[fadeIn_0.15s_ease-out]">
                                        <div className="px-4 py-2.5 border-b border-slate-100 mb-1">
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Signed in as</p>
                                            <p className="text-sm font-bold text-slate-800 truncate">{user?.name}</p>
                                        </div>
                                        <Link to={getDashboardLink()} className="flex items-center gap-2.5 px-4 py-2.5 text-slate-700 hover:bg-indigo-50/50 hover:text-indigo-600 text-sm font-semibold transition-colors">
                                            <LayoutDashboard className="w-4 h-4 text-slate-400" />
                                            My Dashboard
                                        </Link>
                                        <Link to="/dashboard/profile" className="flex items-center gap-2.5 px-4 py-2.5 text-slate-700 hover:bg-indigo-50/50 hover:text-indigo-600 text-sm font-semibold transition-colors">
                                            <User className="w-4 h-4 text-slate-400" />
                                            View Profile
                                        </Link>
                                        <div className="border-t border-slate-100 my-1"></div>
                                        <button 
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-rose-600 hover:bg-rose-50 text-sm font-bold transition-colors text-left"
                                        >
                                            <LogOut className="w-4 h-4 text-rose-400" />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Toggle Button */}
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle Menu"
                        className="md:hidden p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-xl transition-colors focus:outline-none"
                    >
                        {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation Drawer */}
            <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${menuOpen ? 'max-h-[480px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
                <div className="px-4 pt-2 pb-6 space-y-2.5 bg-white border-t border-slate-100 shadow-inner">
                    <div className="flex flex-col gap-1.5 py-2">
                        {navLinks}
                    </div>
                    
                    <div className="border-t border-slate-100 pt-4 space-y-2">
                        {!isAuthenticated ? (
                            <div className="grid grid-cols-2 gap-3">
                                <Link to="/login" className="text-center px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-bold text-sm bg-slate-50 hover:bg-slate-100 transition-colors">
                                    Login
                                </Link>
                                <Link to="/register" className="btn-navbar-uiverse w-full">
                                    <svg
                                        height="20"
                                        width="20"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M0 0h24v24H0z" fill="none"></path>
                                        <path
                                            d="M5 13c0-5.088 2.903-9.436 7-11.182C16.097 3.564 19 7.912 19 13c0 .823-.076 1.626-.22 2.403l1.94 1.832a.5.5 0 0 1 .095.603l-2.495 4.575a.5.5 0 0 1-.793.114l-2.234-2.234a1 1 0 0 0-.707-.293H9.414a1 1 0 0 0-.707.293l-2.234 2.234a.5.5 0 0 1-.793-.114l-2.495-4.575a.5.5 0 0 1 .095-.603l1.94-1.832C5.077 14.626 5 13.823 5 13zm1.476 6.696l.817-.817A3 3 0 0 1 9.414 18h5.172a3 3 0 0 1 2.121.879l.817.817.982-1.8-1.1-1.04a2 2 0 0 1-.593-1.82c.124-.664.187-1.345.187-2.036 0-3.87-1.995-7.3-5-8.96C8.995 5.7 7 9.13 7 13c0 .691.063 1.372.187 2.037a2 2 0 0 1-.593 1.82l-1.1 1.039.982 1.8zM12 13a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
                                            fill="currentColor"
                                        ></path>
                                    </svg>
                                    <span>Register</span>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl mb-2">
                                    <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
                                        {user?.avatar ? <img src={user.avatar} alt="avatar" className="w-full h-full object-cover rounded-full" /> : user?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-800 text-sm">{user?.name}</p>
                                        <p className="text-xs text-slate-500">{user?.email}</p>
                                    </div>
                                </div>
                                <Link to={getDashboardLink()} className="block text-center px-4 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 font-bold text-sm">
                                    My Dashboard
                                </Link>
                                <button 
                                    onClick={handleLogout} 
                                    className="w-full text-center px-4 py-2.5 rounded-xl bg-rose-50 text-rose-600 font-bold text-sm transition-colors"
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;